import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { CheckCircle2, AlertTriangle, ShieldAlert, Activity, Server, Layers } from 'lucide-react';
import GlassPaper from '../../GlassPaper';

function Stat({ icon, label, value, total, tone = 'info', tooltip }) {
    const colors = {
        good:  '#34d399',
        warn:  '#fbbf24',
        crit:  '#f87171',
        info:  '#60a5fa',
        muted: '#94a3b8',
    };
    const content = (
        <Box sx={{
            flex: '1 1 140px',
            minWidth: 140,
            p: { xs: 1.25, sm: 1.5 },
            borderRadius: 3,
            border: '1px solid rgba(148,163,184,0.18)',
            background: 'rgba(15,23,42,0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
        }}>
            <Box sx={{
                width: 36, height: 36, borderRadius: 2,
                background: 'rgba(148,163,184,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: colors[tone],
            }}>{icon}</Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.8, opacity: 0.65, fontWeight: 600 }}>{label}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography sx={{ fontSize: 22, fontWeight: 700, color: colors[tone], fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{value}</Typography>
                    {total != null && <Typography sx={{ fontSize: 12, opacity: 0.55 }}>/ {total}</Typography>}
                </Box>
            </Box>
        </Box>
    );
    return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
}

export default function FleetSummary({ hosts = [], proxmoxNodes = [], vms = [] }) {
    const totalHosts = hosts.length;
    const upHosts = hosts.filter(h => h.available === 1 || h.metrics?.agentPing === 1 || h.metrics?.icmpPing === 1).length;
    const templateMissing = hosts.filter(h => !h.templateOk).length;
    const coverageMissing = hosts.filter(h => h.metricCoverage && !h.metricCoverage.ok && h.metricCoverage.expected?.length > 0).length;
    const withProblems = hosts.filter(h => (h.problems || []).length > 0).length;
    const runningVms = vms.filter(v => v.status === 'running').length;

    return (
        <GlassPaper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, md: 2.5 } }}>
            <Box sx={{
                display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 1.5 },
            }}>
                <Stat
                    icon={<Activity size={18} />}
                    label="Hosts Online"
                    value={upHosts} total={totalHosts}
                    tone={upHosts === totalHosts && totalHosts > 0 ? 'good' : upHosts === 0 ? 'crit' : 'warn'}
                    tooltip={`${upHosts} of ${totalHosts} Zabbix hosts are reachable`}
                />
                <Stat
                    icon={<Server size={18} />}
                    label="Proxmox Nodes"
                    value={proxmoxNodes.filter(n => n.status === 'online').length}
                    total={proxmoxNodes.length}
                    tone={proxmoxNodes.length > 0 && proxmoxNodes.every(n => n.status === 'online') ? 'good' : 'warn'}
                />
                <Stat
                    icon={<Layers size={18} />}
                    label="VMs Running"
                    value={runningVms}
                    total={vms.length}
                    tone="info"
                />
                <Stat
                    icon={<ShieldAlert size={18} />}
                    label="Template Gaps"
                    value={templateMissing}
                    tone={templateMissing === 0 ? 'good' : 'warn'}
                    tooltip="Hosts with missing or unrecognized templates in Zabbix"
                />
                <Stat
                    icon={<AlertTriangle size={18} />}
                    label="Metric Gaps"
                    value={coverageMissing}
                    tone={coverageMissing === 0 ? 'good' : 'warn'}
                    tooltip="Hosts with expected metrics (CPU/mem/disk/uptime) missing"
                />
                <Stat
                    icon={<CheckCircle2 size={18} />}
                    label="Active Problems"
                    value={withProblems}
                    tone={withProblems === 0 ? 'good' : 'crit'}
                    tooltip="Hosts with open Zabbix triggers"
                />
            </Box>
        </GlassPaper>
    );
}
